import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  User, 
  MoreHorizontal, 
  Eye, 
  Copy, 
  Edit, 
  Share2, 
  Trash2,
  Check,
  Lock,
  Building2,
  Users,
  Sparkles
} from "lucide-react";
import type { PersonaWithPermissions } from "@/lib/persona-types";
import { cn } from "@/lib/utils";

interface PersonaCardProps {
  persona: PersonaWithPermissions;
  isSelected?: boolean;
  onSelect?: () => void;
  onView: () => void;
  onDuplicate: () => void;
  onEdit?: () => void;
  onShare?: () => void;
  onDelete?: () => void;
}

const shareBadgeConfig = {
  Private: { icon: Lock, className: "bg-muted text-muted-foreground" },
  Org: { icon: Building2, className: "bg-primary/20 text-primary" },
  Shared: { icon: Users, className: "bg-accent/20 text-accent-foreground" },
  Template: { icon: Sparkles, className: "bg-reqqy-warning/20 text-reqqy-warning" },
  Project: { icon: Users, className: "bg-secondary text-secondary-foreground" },
};

export function PersonaCard({
  persona,
  isSelected,
  onSelect,
  onView,
  onDuplicate,
  onEdit,
  onShare,
  onDelete,
}: PersonaCardProps) {
  const BadgeIcon = shareBadgeConfig[persona.share_badge].icon;

  return (
    <Card 
      className={cn(
        "group relative transition-all card-hover",
        isSelected && "border-primary ring-2 ring-primary/20"
      )}
    >
      <CardContent className="p-4">
        {/* Header with avatar and actions */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-sm truncate">{persona.name}</h4>
              <p className="text-xs text-muted-foreground truncate">{persona.role}</p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
              {onSelect && (
                <DropdownMenuItem onClick={onSelect}>
                  <Check className="w-4 h-4 mr-2" />
                  {isSelected ? "Deselect" : "Select for Run"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onView}>
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              
              {persona.can_edit && onEdit && (
                <DropdownMenuItem onClick={onEdit}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              
              {persona.can_share && onShare && (
                <DropdownMenuItem onClick={onShare}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
              )}

              {persona.can_delete && onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={onDelete}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tags */}
        {persona.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {persona.tags.slice(0, 3).map((tag, i) => (
              <Badge key={i} variant="secondary" className="text-xs px-2 py-0">
                {tag}
              </Badge>
            ))}
            {persona.tags.length > 3 && (
              <Badge variant="secondary" className="text-xs px-2 py-0">
                +{persona.tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* Goals preview */}
        {persona.goals.length > 0 && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {persona.goals[0]}
            {persona.goals.length > 1 && ` +${persona.goals.length - 1} more`}
          </p>
        )}

        {/* Footer with badge and select button */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Badge 
            variant="outline" 
            className={cn("text-xs", shareBadgeConfig[persona.share_badge].className)}
          >
            <BadgeIcon className="w-3 h-3 mr-1" />
            {persona.share_badge}
          </Badge>

          {onSelect && (
            <Button 
              variant={isSelected ? "default" : "outline"} 
              size="sm"
              onClick={onSelect}
              className="h-7 text-xs"
            >
              {isSelected ? (
                <>
                  <Check className="w-3 h-3 mr-1" />
                  Selected
                </>
              ) : (
                "Select"
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
